import { Controller, useFormContext } from "react-hook-form";

import type { WorkflowTriggerType } from "@blis/shared/schemas/workflow";
import { trpc } from "@blis/trpc/react";

import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import { FieldError, Input, TextField } from "@/components/ui/field";
import { Select, SelectItem, SelectTrigger } from "@/components/ui/select";
import type { FormValues } from "./schema";

type ComponentMap = Record<
  keyof typeof WorkflowTriggerType,
  {
    Facts: React.FC;
    Operators: React.FC<{ parentIndex: number; index: number }>;
    Value: React.FC<{ parentIndex: number; index: number }>;
  }
>;

export const eventComponents: ComponentMap = {
  INITIAL_RESULT: {
    Facts: function InitialResultFacts() {
      return (
        <>
          <SelectItem id="orgId">Client</SelectItem>
          <SelectItem id="resultId">Initial result</SelectItem>
          <SelectItem id="resultNumeric">Initial result (numeric)</SelectItem>
          <SelectItem id="sampleTransfusionId">Transfusion status</SelectItem>
        </>
      );
    },
    Operators: ResultOperators,
    Value: ValueInput,
  },
  FINAL_RESULT: {
    Facts: function FinalResultFacts() {
      return (
        <>
          <SelectItem id="orgId">Client</SelectItem>
          <SelectItem id="finalResultId">Final result</SelectItem>
          <SelectItem id="finalResultNumeric">
            Final result (numeric)
          </SelectItem>
          <SelectItem id="sampleTransfusionId">Transfusion status</SelectItem>
        </>
      );
    },
    Operators: ResultOperators,
    Value: ValueInput,
  },
};

function ResultOperators({
  parentIndex,
  index,
}: {
  parentIndex: number;
  index: number;
}) {
  const { watch } = useFormContext<FormValues>();
  const fact = watch(`condition.${parentIndex}.or.${index}.fact`);

  switch (fact) {
    case "orgId":
    case "resultId":
    case "finalResultId":
    case "sampleTransfusionId":
      return <TextOperators />;
    case "resultNumeric":
    case "finalResultNumeric":
      return <NumericOperators />;
  }

  return null;
}

function TextResultValueInput({
  parentIndex,
  index,
}: {
  parentIndex: number;
  index: number;
}) {
  const { control } = useFormContext<FormValues>();
  const { data: results } = trpc.admin.testResult.list.useQuery({
    category: "TEXT",
  });

  return (
    <Controller
      control={control}
      name={`condition.${parentIndex}.or.${index}.value`}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <ComboBox
          menuTrigger="focus"
          {...field}
          aria-label={`Value for condition ${index} of ${parentIndex}`}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <ComboBoxInput ref={ref} />
          <ComboBoxList items={results || []}>
            {(item) => (
              <ComboBoxItem id={item.value}>{item.description}</ComboBoxItem>
            )}
          </ComboBoxList>
          <FieldError>{error?.message}</FieldError>
        </ComboBox>
      )}
    />
  );
}

function NumericResultValueInput({
  parentIndex,
  index,
}: {
  parentIndex: number;
  index: number;
}) {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name={`condition.${parentIndex}.or.${index}.value`}
      render={({ field, fieldState: { error } }) => (
        <TextField
          type="number"
          {...field}
          aria-label={`Value for condition ${index} of ${parentIndex}`}
          value={field.value?.toString()}
          onChange={(value) => field.onChange(value)}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Input />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function OrgIdValueInput({
  parentIndex,
  index,
}: {
  parentIndex: number;
  index: number;
}) {
  const { control } = useFormContext<FormValues>();
  const { data: results } = trpc.admin.org.list.useQuery();

  return (
    <Controller
      control={control}
      name={`condition.${parentIndex}.or.${index}.value`}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <ComboBox
          menuTrigger="focus"
          {...field}
          aria-label={`Value for condition ${index} of ${parentIndex}`}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <ComboBoxInput ref={ref} />
          <ComboBoxList items={results || []}>
            {(item) => (
              <ComboBoxItem id={item.id} textValue={item.name}>
                {item.name}
              </ComboBoxItem>
            )}
          </ComboBoxList>
          <FieldError>{error?.message}</FieldError>
        </ComboBox>
      )}
    />
  );
}

const transfusionOptions = [
  { value: "unknown", description: "Unknown" },
  { value: "pre", description: "Pre" },
  { value: "post", description: "Post" },
];

function TransfusionIdValueInput({
  parentIndex,
  index,
}: {
  parentIndex: number;
  index: number;
}) {
  const { control } = useFormContext<FormValues>();

  return (
    <Controller
      control={control}
      name={`condition.${parentIndex}.or.${index}.value`}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <ComboBox
          menuTrigger="focus"
          {...field}
          aria-label={`Value for condition ${index} of ${parentIndex}`}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <ComboBoxInput ref={ref} />
          <ComboBoxList items={transfusionOptions}>
            {(item) => (
              <ComboBoxItem id={item.value} textValue={item.description}>
                {item.description}
              </ComboBoxItem>
            )}
          </ComboBoxList>
          <FieldError>{error?.message}</FieldError>
        </ComboBox>
      )}
    />
  );
}

function ValueInput({
  parentIndex,
  index,
}: {
  parentIndex: number;
  index: number;
}) {
  const { watch } = useFormContext<FormValues>();
  const fact = watch(`condition.${parentIndex}.or.${index}.fact`);

  if (fact?.toLowerCase().includes("orgid")) {
    return <OrgIdValueInput parentIndex={parentIndex} index={index} />;
  }

  if (fact?.toLowerCase().includes("sampletransfusionid")) {
    return <TransfusionIdValueInput parentIndex={parentIndex} index={index} />;
  }

  if (fact?.toLowerCase().includes("resultid")) {
    return <TextResultValueInput parentIndex={parentIndex} index={index} />;
  }

  if (fact?.toLowerCase().includes("resultnumeric")) {
    return <NumericResultValueInput parentIndex={parentIndex} index={index} />;
  }

  return (
    <Select
      isDisabled
      aria-label="Placeholder select  disabled"
      placeholder=" "
    >
      <SelectTrigger />
    </Select>
  );
}

function TextOperators() {
  return (
    <>
      <SelectItem id="eq">is</SelectItem>
      <SelectItem id="neq">is not</SelectItem>
    </>
  );
}

function NumericOperators() {
  return (
    <>
      <SelectItem id="eq">=</SelectItem>
      <SelectItem id="neq">!=</SelectItem>
      <SelectItem id="gt">{">"}</SelectItem>
      <SelectItem id="gte">{">="}</SelectItem>
      <SelectItem id="lt">{"<"}</SelectItem>
      <SelectItem id="lte">{"<="}</SelectItem>
    </>
  );
}
